import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import AICostBadge from '../AICostBadge';
import { Sparkles, Loader2, TrendingUp, AlertCircle, Lightbulb, Target } from 'lucide-react';

const AIBusinessSummary = ({ formData, outputs, competitionData, API, onSummaryGenerated, isDemoPlan, existingSummary }) => {
 const [loading, setLoading] = useState(false);
 const [summary, setSummary] = useState(existingSummary || null);
 const [error, setError] = useState(null);

 // Sync the pre-baked demo summary into local state whenever the parent
 // hands us one (loadExample fires this). We deliberately do NOT auto-hit
 // the AI endpoint in demo mode — that's the whole point of the feature.
 useEffect(() => {
   if (existingSummary) setSummary(existingSummary);
 }, [existingSummary]);

 // Auto-generate summary when outputs are available — but skip demo
 // plans (they carry a pre-baked snapshot) so we never spend AI credits
 // on the "Try me" example.
 useEffect(() => {
   if (isDemoPlan) return;
   if (outputs && !summary && !loading && !error) {
     generateSummary();
   }
 }, [outputs, isDemoPlan]); // eslint-disable-line

 const generateSummary = async () => {
   // Demo plans never touch the paid endpoint — the parent-supplied
   // snapshot is what visitors see. If they want a live analysis they
   // have to click New Plan first, at which point isDemoPlan flips off.
   if (isDemoPlan) {
     toast?.info?.('This is a demo snapshot. Start your own plan to run a live AI analysis.');
     return;
   }
   setLoading(true);
   setError(null);
 
 try {
 // Get token from localStorage and include in headers
 const token = localStorage.getItem('token');
 const headers = token ? {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 } : {
 'Content-Type': 'application/json'
 };

 const response = await axios.post(`${API}/calculator/ai-business-summary`, {
 form_data: formData,
 outputs: outputs,
 competition_data: competitionData
 }, {
 headers: headers
 });

 setSummary(response.data);
 // Notify parent component of the summary
 if (onSummaryGenerated) {
 onSummaryGenerated(response.data);
 }
 } catch (err) {
 console.error('Error generating AI summary:', err);
 setError('Failed to generate business summary. Please try again.');
 } finally {
 setLoading(false);
 }
 };

 return (
 <Card className="border-2 border-primary/30 dark:border-primary/30 bg-pro-soft dark:/20 dark:to-indigo-900/20" data-testid="ai-business-summary">
 <CardHeader>
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
 <Sparkles className="w-6 h-6 text-white" />
 </div>
 <div>
 <CardTitle className="text-lg font-bold text-primary dark:text-primary flex items-center gap-2">
 AI Business Recommendations
 {isDemoPlan && (
   <span
     className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-300 border border-amber-500/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
     data-testid="ai-demo-badge"
   >
     Demo snapshot · no credits used
   </span>
 )}
 </CardTitle>
 <CardDescription className="text-sm text-primary dark:text-primary">
 {isDemoPlan
   ? 'Preview of the analysis your own plan will produce — no AI credits consumed.'
   : 'Get personalized insights based on your financial projections'}
 </CardDescription>
 </div>
 </div>
 </div>
 </CardHeader>
 <CardContent className="space-y-4">
 {!summary && !loading && (
 <div className="text-center py-6">
 <p className="text-sm text-primary dark:text-primary mb-4">
 Generate an AI-powered business analysis with actionable recommendations to improve your freeze-drying business.
 </p>
 <Button 
 onClick={generateSummary}
 className="bg-primary hover:bg-primary/90 text-white"
 size="lg"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 Generate AI Business Summary
 </Button>
 <div className="mt-3 flex justify-center">
 <AICostBadge tool="calculator.business_summary" />
 </div>
 </div>
 )}

 {loading && (
 <div className="flex items-center justify-center py-8">
 <Loader2 className="w-8 h-8 animate-spin text-primary" />
 <span className="ml-3 text-primary dark:text-primary">Analyzing your business model...</span>
 </div>
 )}

 {error && (
 <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
 <div className="flex items-center gap-2">
 <AlertCircle className="w-5 h-5 text-red-600" />
 <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
 </div>
 </div>
 )}

 {summary && (
 <div className="space-y-4">
 {/* Overall Assessment */}
 <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border-2 border-primary/30 dark:border-primary/30">
 <div className="flex items-start gap-3 mb-3">
 <TrendingUp className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
 <div>
 <h3 className="font-bold text-lg text-primary dark:text-primary mb-2">
 Overall Business Assessment
 </h3>
 <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
 {summary.overall_assessment}
 </p>
 </div>
 </div>
 </div>

 {/* Key Strengths */}
 {summary.strengths && summary.strengths.length > 0 && (
 <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
 <div className="flex items-start gap-3">
 <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
 <div className="flex-1">
 <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Key Strengths</h4>
 <ul className="space-y-2">
 {summary.strengths.map((strength, idx) => (
 <li key={idx} className="text-sm text-green-800 dark:text-green-200 flex items-start gap-2">
 <span className="text-green-600 font-bold">✓</span>
 <span>{strength}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 )}

 {/* Areas for Improvement */}
 {summary.areas_for_improvement && summary.areas_for_improvement.length > 0 && (
 <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
 <div className="flex-1">
 <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Areas for Improvement</h4>
 <ul className="space-y-2">
 {summary.areas_for_improvement.map((area, idx) => (
 <li key={idx} className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
 <span className="text-amber-600 font-bold">⚠</span>
 <span>{area}</span>
 </li>
 ))}
 </ul>
 </div>
 </div>
 </div>
 )}

 {/* Actionable Recommendations */}
 {summary.recommendations && summary.recommendations.length > 0 && (
 <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
 <div className="flex items-start gap-3">
 <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
 <div className="flex-1">
 <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Actionable Recommendations</h4>
 <ol className="space-y-3">
 {summary.recommendations.map((rec, idx) => (
 <li key={idx} className="text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2">
 <span className="text-blue-600 font-bold flex-shrink-0">{idx + 1}.</span>
 <span>{rec}</span>
 </li>
 ))}
 </ol>
 </div>
 </div>
 </div>
 )}

 {/* Regenerate Button */}
 <div className="flex justify-center pt-2">
 {isDemoPlan ? (
   <p className="text-xs text-muted-foreground italic text-center max-w-md" data-testid="ai-demo-hint">
     This is a static preview. Click <b>New Plan</b> to enter your own numbers — the AI will run for real and consume credits from that point on.
   </p>
 ) : (
 <Button 
 onClick={generateSummary}
 variant="outline"
 className="border-primary/30 dark:border-primary/30 text-primary dark:text-primary hover:bg-primary/5 dark:hover:bg-primary/10"
 >
 <Sparkles className="w-4 h-4 mr-2" />
 Regenerate Analysis
 </Button>
 )}
 </div>
 </div>
 )}

 {/* Disclaimer */}
 <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 border border-slate-200 dark:border-slate-700">
 <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
 <strong>Note:</strong> AI-generated recommendations are for informational purposes only and should not be 
 considered professional business, financial, or investment advice. Always consult with qualified professionals 
 before making significant business decisions.
 </p>
 </div>
 </CardContent>
 </Card>
 );
};

export default AIBusinessSummary;
